/**
 * ReportListingButton (Client Island) — D4.
 *
 * A signed-in customer reports a listing to staff with a typed reason
 * (`listing.v1.ListingReportReason`) and optional details. The server is the
 * only judge of whether the report is admitted — the listing must be public,
 * not the reporter's own, not already reported by them while their report is
 * open, and within their daily limit — and every refusal comes back as a
 * typed `account.v1.listing_reports_*` reason this dialog localizes. Nothing
 * here restates a rule.
 *
 * Hidden for the lister (a lister cannot report their own listing, and
 * offering the control would only earn them a refusal). Auth-gated through
 * the seam's `requireAuth`: a signed-out click opens the sign-in modal, and
 * the dialog opens once the visitor is signed in.
 */
import { localizedToast } from '@/shared/i18n/toast';
import { useLocalizeApiError } from '@/shared/i18n/useLocalizeApiError';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { bff } from '@/shared/lib/bff';
import { listingReportReasonOptions } from '@/shared/lib/listings/report-reasons';
import type { StatedListingReportReasonName } from '@/shared/lib/types/listing';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import { bffError } from '@letsgethome/lib/server-errors';
import type { ListingId } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@letsgethome/ui/dialog';
import { SelectField, TextareaField } from '@letsgethome/ui/forms';
import { useMutation } from '@tanstack/react-query';
import { Flag } from 'lucide-react';
import { useState } from 'react';

/** The wire's own bound on `details` (`ReportListingRequest.details`). */
const DETAILS_MAX = 1000;

type Props = {
  listingId: ListingId;
  isLister: boolean;
};

export function ReportListingButton({ listingId, isLister }: Props) {
  const t = useTranslations('listings.report');
  const locale = useLocale();
  const localizeApiError = useLocalizeApiError();
  const { requireAuth } = useAuthGate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<StatedListingReportReasonName | ''>('');
  const [details, setDetails] = useState('');

  const close = () => {
    setOpen(false);
    setReason('');
    setDetails('');
  };

  const mutation = useMutation({
    mutationFn: async (picked: StatedListingReportReasonName) => {
      const trimmed = details.trim();
      const result = await bff('/api/listings/report', {
        listingId,
        reason: picked,
        // Blank is "nothing to add": the field is omitted, never sent empty.
        ...(trimmed === '' ? {} : { details: trimmed }),
      });
      if (!result.success || !result.data) throw bffError(result.error);
      return result.data;
    },
    onSuccess: () => {
      localizedToast.success(t('sent'));
      close();
    },
    onError: (err) => localizedToast.error(localizeApiError(err, 'listings.report.failed')),
  });

  if (isLister) return null;

  const options = listingReportReasonOptions(locale);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        data-testid="report-listing-open"
        onClick={() => {
          if (requireAuth(() => setOpen(true))) setOpen(true);
        }}
      >
        <Flag className="mr-2 h-4 w-4" />
        {t('open')}
      </Button>
      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <DialogContent data-testid="report-listing-dialog">
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <SelectField<StatedListingReportReasonName>
              label={t('reasonLabel')}
              placeholder={t('reasonPlaceholder')}
              options={options}
              value={reason}
              onValueChange={setReason}
            />
            <TextareaField
              label={t('detailsLabel')}
              hint={t('detailsCount', { count: details.length, max: DETAILS_MAX })}
              value={details}
              maxLength={DETAILS_MAX}
              rows={4}
              onChange={(e) => setDetails(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              {t('cancel')}
            </Button>
            <Button
              type="button"
              data-testid="report-listing-submit"
              disabled={reason === '' || mutation.isPending}
              onClick={() => {
                if (reason !== '') mutation.mutate(reason);
              }}
            >
              {t('submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
